import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { CheckCircle2, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPlan } from "@/hooks/useUserPlan";
import { supabase } from "@/integrations/supabase/client";
import { phCapture } from "@/lib/posthog";
import { toast } from "sonner";

const tiers = [
  {
    name: "Free",
    price: "£0",
    period: "",
    features: [
      { text: "1 starter report", included: true },
      { text: "Public form access", included: true },
      { text: "Starter experience only", included: true },
      { text: "PDF export", included: false },
      { text: "Market Radar", included: false },
      { text: "Price Radar", included: false },
      { text: "Saved report history", included: false },
    ],
    ctaLabel: "Create free account",
  },
  {
    name: "Pro",
    price: "£19",
    period: "/month",
    highlight: true,
    features: [
      { text: "25 reports per month", included: true },
      { text: "Full copy pack", included: true },
      { text: "Evidence links", included: true },
      { text: "Market Radar", included: true },
      { text: "Price Radar", included: true },
      { text: "PDF export", included: true },
      { text: "Saved report history", included: true },
      { text: "Manage subscription", included: true },
    ],
    ctaLabel: "Start Pro",
  },
];

const faqs = [
  { q: "What does ScopSift analyse?", a: "ScopSift reviews public buyer chatter, reviews, and your optional pasted review text to surface themes, objections, buyer wording, and copy angles." },
  { q: "Do I need an account?", a: "You can browse the form before signing in, but a free account is required to generate reports." },
  { q: "What do I get on the free plan?", a: "The free plan gives you a starter experience with 1 starter report." },
  { q: "What does Pro unlock?", a: "Pro unlocks the full copy pack, evidence links, Market Radar, Price Radar, PDF export, and saved history." },
  { q: "How long does a report take?", a: "Most reports should complete in a short time, but timing can vary depending on the sources and features used." },
  { q: "Are the outputs guaranteed?", a: "No. Outputs are suggestions informed by available evidence and should always be reviewed before publishing." },
];

const Pricing = () => {
  const { user, session } = useAuth();
  const { plan, loading: planLoading } = useUserPlan();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const isPro = plan === "pro";

  // Show success toast when returning from Stripe checkout
  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      window.history.replaceState(null, "", "/pricing");
      toast.custom((id) => (
        <div className="w-[360px] rounded-lg border border-teal-400/30 bg-slate-900 p-5 shadow-xl">
          <h3 className="text-lg font-bold text-white mb-1">Welcome to ScopSift Pro</h3>
          <p className="text-sm text-slate-300 leading-relaxed mb-4">
            Pro is now active. You've unlocked full copy packs, evidence links,
            Market Radar, Price Radar, PDF export, and saved history.
          </p>
          <Button
            className="w-full btn-gradient"
            onClick={() => {
              toast.dismiss(id);
              navigate("/generate");
            }}
          >
            Generate your first Pro report
          </Button>
        </div>
      ), { duration: 15000 });
    }
  }, [searchParams, navigate]);

  const handleProClick = async () => {
    // Signed-out: send to sign-in with returnTo
    if (!user) {
      sessionStorage.setItem("auth_return_to", "/pricing");
      navigate("/signin?returnTo=/pricing", { state: { returnTo: "/pricing" } });
      return;
    }

    // Already Pro: do nothing
    if (isPro) return;

    // Start checkout
    phCapture("upgrade_clicked", { source: "pricing" });
    console.log("[ScopSift] Starting Stripe checkout for user:", user.id);
    toast.info("Creating Stripe checkout…");
    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast.error(err.message || "Failed to start checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  const ctaTo = "/auth";

  return (
    <Layout>
      <div className="dark-page min-h-[80vh]">
        <section className="mx-auto max-w-4xl px-4 md:px-6 py-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-2 text-4xl font-extrabold tracking-tight text-white">
              Pricing
            </h1>
            <p className="mb-10 text-sm leading-7 text-slate-200/90">
              Simple plans. Start free, upgrade when you're ready.
            </p>

            <div className="grid gap-6 md:grid-cols-2">
              {tiers.map((tier) => {
                const isProTier = tier.name === "Pro";

                return (
                  <div
                    key={tier.name}
                    className={`card-surface text-left flex flex-col ${tier.highlight ? "ring-1 ring-teal-400/50" : ""}`}
                  >
                    <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                    <div className="mt-2 mb-6">
                      <span className="text-2xl font-bold text-white">{tier.price}</span>
                      {tier.period && (
                        <span className="text-sm text-slate-400">{tier.period}</span>
                      )}
                    </div>
                    <ul className="space-y-2 text-sm">
                      {tier.features.map((f) => (
                        <li key={f.text} className={`flex items-start gap-2 leading-7 ${f.included ? "text-slate-200/90" : "text-slate-500"}`}>
                          {f.included ? (
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />
                          ) : (
                            <X className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
                          )}
                          {f.text}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-6">
                      {isProTier ? (
                        <Button
                          className={
                            isPro
                              ? "w-full bg-teal-900/30 text-teal-300 border border-teal-400/30 cursor-default"
                              : "w-full btn-gradient"
                          }
                          disabled={isPro || checkoutLoading || planLoading}
                          onClick={handleProClick}
                        >
                          {planLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : isPro ? (
                            "Current plan"
                          ) : checkoutLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Redirecting…
                            </>
                          ) : (
                            tier.ctaLabel
                          )}
                        </Button>
                      ) : (
                        <Button
                          asChild
                          className="w-full border-white/10 text-slate-200 hover:text-white hover:bg-white/5 bg-transparent border"
                          variant="outline"
                        >
                          <Link to={ctaTo}>{tier.ctaLabel}</Link>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-14 text-left">
              <h2 className="mb-6 text-2xl font-bold text-white text-center">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="w-full card-surface">
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                    <AccordionTrigger className="text-white hover:text-white/90 hover:no-underline">{faq.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-200/90">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Pricing;
